/*!

=========================================================
* Light Bootstrap Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { Component, useEffect, useMemo, useState } from "react";
import { Grid, Col, Row } from "react-bootstrap";
// react component used to create charts
import ChartistGraph from "react-chartist";
// react components used to create a SVG / Vector map
import { VectorMap } from "react-jvectormap";

import Card from "components/Card/Card.jsx";
import StatsCard from "components/Card/StatsCard.jsx";
import Tasks from "components/Tasks/Tasks.jsx";
import { searchOrders } from "../Listfunction";
import { fetchOperationalDashboard } from "api/medicineOrderImproved/medicineOrderImprovedApi";
import "./dashboardOperational.css";

import {
  dataPie,
  dataSales,
  optionsSales,
  responsiveSales,
  dataBar,
  optionsBar,
  responsiveBar,
  table_data,
} from "variables/Variables.jsx";

var mapData = {
  AU: 760,
  BR: 550,
  CA: 120,
  DE: 1300,
  FR: 540,
  GB: 690,
  GE: 200,
  IN: 200,
  RO: 600,
  RU: 300,
  US: 2920,
};

const OPERATIONAL_DASHBOARD_ROLES = [1, 2, 11, 13, 18];
const INITIAL_QUEUE_ITEMS = 10;
const QUEUE_REVEAL_STEP = 20;
const DASHBOARD_QUEUE_LIMIT = 60;

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const canViewOperationalDashboard = (user) => {
  const role = Number(user && user.role);
  const roleName = String(
    (user && (user.role_name || user.user_role || user.userType || user.type)) || "",
  ).toLowerCase();

  return (
    OPERATIONAL_DASHBOARD_ROLES.includes(role) ||
    roleName.includes("super") ||
    roleName === "admin" ||
    roleName.includes("pharmacist") ||
    roleName.includes("procurement")
  );
};

const formatDashboardValue = (card) => {
  if (!card) return "0";
  if (card.type === "money") {
    return Number(card.value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return Number(card.value || 0).toLocaleString();
};

const formatQueueTitle = (item) => {
  if (!item) return "";

  const rawTitle = String(item.title || "").trim();
  const displayId =
    (item.meta && item.meta.display_id) ||
    item.order_id ||
    (item.meta && item.meta.order_id) ||
    (item.meta && item.meta.ticket_id) ||
    item.id;

  if (!displayId) {
    return rawTitle;
  }

  const displayText = String(displayId);

  if (rawTitle.includes(displayText)) {
    return rawTitle;
  }

  if (new RegExp(`#${displayText}\\b`).test(rawTitle)) {
    return rawTitle;
  }

  return rawTitle ? `${rawTitle} #${displayText}` : `Order #${displayText}`;
};

const getDashboardIcon = (key) =>
  ({
    submitted: "fa fa-inbox",
    processing: "fa fa-cogs",
    processing_overdue: "fa fa-hourglass-half",
    item_pending: "fa fa-exclamation-circle",
    item_pending_overdue: "fa fa-exclamation-triangle",
    packed: "fa fa-archive",
    packed_overdue: "fa fa-clock-o",
    dispatched_overdue: "fa fa-truck",
    procurement_ready: "fa fa-shopping-basket",
    b2b_today: "fa fa-building",
    payment_pending: "fa fa-credit-card",
    payment_overdue_45: "fa fa-warning",
    pending_amount: "fa fa-money",
    tickets_to_resolve: "fa fa-ticket",
    high_priority_tickets: "fa fa-flag",
    claims_submitted: "fa fa-file-text-o",
    claims_reviewed: "fa fa-check-square-o",
    claims_overdue: "fa fa-hourglass-end",
    aging_processing: "fa fa-clock-o",
  }[key] || "fa fa-circle");

const visibleDashboardCards = (cards) =>
  (cards || []).filter((card) => card.key !== "claim_invoice_follow_up");

const visibleDashboardQueues = (queues) =>
  (queues || []).filter((queue) => queue.key !== "claim_reconciliation_queue");

const OperationalDashboard = () => {
  const [dashboard, setDashboard] = useState({ cards: [], queues: [] });
  const [visibleQueueCounts, setVisibleQueueCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const user = useMemo(() => getStoredUser(), []);

  useEffect(() => {
    if (!user.token) {
      setError("Please log in again before loading dashboard.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    fetchOperationalDashboard(user.token, { queueLimit: DASHBOARD_QUEUE_LIMIT })
      .then((payload) => {
        const nextDashboard = payload.data || { cards: [], queues: [] };
        const nextVisibleCounts = {};

        (nextDashboard.queues || []).forEach((queue) => {
          nextVisibleCounts[queue.key] = INITIAL_QUEUE_ITEMS;
        });

        setDashboard(nextDashboard);
        setVisibleQueueCounts(nextVisibleCounts);
      })
      .catch(() => setError("Unable to load operational dashboard."))
      .finally(() => setLoading(false));
  }, [user.token]);

  const handleDashboardLink = (event, item) => {
    const ticketId = item && item.meta && item.meta.ticket_id;

    if (ticketId) {
      localStorage.setItem("tic_id", ticketId);
    }
  };

  const showMoreQueueItems = (queueKey) => {
    setVisibleQueueCounts((current) => ({
      ...current,
      [queueKey]: (current[queueKey] || INITIAL_QUEUE_ITEMS) + QUEUE_REVEAL_STEP,
    }));
  };

  return (
    <div className="main-content operational-dashboard">
      <div className="opd-shell">
        <div className="opd-head">
          <div>
            <p>Operational Dashboard</p>
            <h3>Today&apos;s Work Queue</h3>
          </div>
          <div className="opd-head-actions">
            <a href="/admin/operations-loading">Open loading dashboard</a>
            <span>{dashboard.generated_at ? new Date(dashboard.generated_at).toLocaleString() : ""}</span>
          </div>
        </div>

        {error ? <div className="opd-alert">{error}</div> : null}

        <div className="opd-metric-grid">
          {visibleDashboardCards(dashboard.cards).map((card) => (
            <a
              className={`opd-metric-card ${card.tone ? `opd-tone-${card.tone}` : ""}`}
              href={card.link || "#"}
              key={card.key}
              data-money={card.type === "money"}
            >
              <i className={getDashboardIcon(card.key)} />
              <span>{card.label}</span>
            <strong>{loading ? "-" : formatDashboardValue(card)}</strong>
              <em>{card.helper}</em>
            </a>
          ))}
          {loading && !visibleDashboardCards(dashboard.cards).length ? (
            <div className="opd-metric-card">
              <i className="fa fa-refresh" />
              <span>Loading</span>
              <strong>-</strong>
              <em>Preparing your dashboard</em>
            </div>
          ) : null}
        </div>

        <div className="opd-panel-grid">
          {visibleDashboardQueues(dashboard.queues).map((queue) => {
            const allQueueItems = queue.items || [];
            const queueTotal = Number(queue.total || allQueueItems.length || 0);
            const visibleCount = visibleQueueCounts[queue.key] || INITIAL_QUEUE_ITEMS;
            const queueItems = allQueueItems.slice(0, visibleCount);
            const canShowMore = queueItems.length < allQueueItems.length;

            return (
            <section className="opd-panel" key={queue.key}>
              <div>
                <h4>{queue.key === "order_stage_aging" ? "Operations Aging" : queue.label}</h4>
                <p>
                  Showing {queueItems.length} of {queueTotal} item(s)
                  {queue.has_more ? `, first ${queue.limit || allQueueItems.length} loaded` : ""}
                </p>
              </div>
              <div className="opd-queue">
                {queueItems.length ? (
                  queueItems.map((item) => (
                    <a
                      className={item.tone ? `opd-tone-${item.tone}` : ""}
                      href={item.link || "#"}
                      key={`${queue.key}-${item.id}`}
                      onClick={(event) => handleDashboardLink(event, item)}
                    >
                      <div className="opd-queue-main">
                        <strong>{formatQueueTitle(item)}</strong>
                        {item.message ? <em>{item.message}</em> : null}
                      </div>
                      <span>{item.subtitle}</span>
                    </a>
                  ))
                ) : (
                  <span className="opd-empty">No urgent items right now.</span>
                )}
              </div>
              {canShowMore ? (
                <button
                  className="opd-show-more"
                  onClick={() => showMoreQueueItems(queue.key)}
                  type="button"
                >
                  Show more
                </button>
              ) : null}
              {!canShowMore && queue.has_more ? (
                <span className="opd-more-note">
                  More records are available in the full list.
                </span>
              ) : null}
            </section>
          );
          })}
        </div>
      </div>
    </div>
  );
};

class LegacyDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      totalSalesToday: 0,
      totalSalesYesterday: 0,
    };
  }

  createTableData() {
    var tableRows = [];
    for (var i = 0; i < table_data.length; i++) {
      tableRows
        .push
        // <tr key={i}>
        //   <td>
        //     <div className="flag">
        //       <img src={table_data[i].flag} alt="us_flag" />
        //     </div>
        //   </td>
        //   <td>{table_data[i].country}</td>
        //   <td className="text-right">{table_data[i].count}</td>
        //   <td className="text-right">{table_data[i].percentage}</td>
        // </tr>
        ();
    }
    return tableRows;
  }

  componentDidMount() {
    var today = new Date();
    var dd = String(today.getDate()).padStart(2, "0");
    var mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
    var yyyy = today.getFullYear();
    today = yyyy + "-" + mm + "-" + dd;

    var yesterday = new Date(Date.now() - 864e5);
    var dd = String(yesterday.getDate()).padStart(2, "0");
    var mm = String(yesterday.getMonth() + 1).padStart(2, "0"); //January is 0!
    var yyyy = yesterday.getFullYear();
    yesterday = yyyy + "-" + mm + "-" + dd;

    this.getDashboardData(today, "today");
    this.getDashboardData(yesterday, "yesterday");
  }

  getDashboardData = (date, orderDate) => {
    var token = JSON.parse(localStorage.getItem("user"));
    const obj = token.token;

    searchOrders("", "", "", "", date, "", "", obj)
      .then((response) => {
        if (orderDate == "today") {
          this.setState({
            totalSalesToday: response.data.total,
          });
        } else {
          this.setState({
            totalSalesYesterday: response.data.total,
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  render() {
    return (
      <div className="main-content">
        <Grid fluid>
          <Row>
            <Col lg={3} sm={6}>
              <StatsCard
                bigIcon={<i className="pe-7s-server text-warning" />}
                statsText="&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Total Orders today"
                statsValue={this.state.totalSalesToday}
                statsIcon={<i className="fa fa-refresh" />}
                statsIconText="Updated now"
              />
            </Col>
            <Col lg={3} sm={6}>
              <StatsCard
                bigIcon={<i className="pe-7s-wallet text-success" />}
                statsText="&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Yesterday's Total Orders"
                statsValue={this.state.totalSalesYesterday}
                statsIcon={<i className="fa fa-calendar-o" />}
                statsIconText="Last day"
              />
            </Col>
            <Col lg={3} sm={6}>
              <StatsCard
                bigIcon={<i className="pe-7s-graph1 text-danger" />}
                statsText="Last Week Orders"
                statsValue="23"
                statsIcon={<i className="fa fa-clock-o" />}
                statsIconText="In the last hour"
              />
            </Col>
            <Col lg={3} sm={6}>
              <StatsCard
                bigIcon={<i className="fa fa-twitter text-info" />}
                statsText="Last Month's Orders"
                statsValue="+45"
                statsIcon={<i className="fa fa-refresh" />}
                statsIconText="Updated now"
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Card
                title="Total Orders & Sales Today"
                // category="All products that were shipped"
                content={
                  <Row>
                    <Col md={5}>
                      <div className="table-responsive">
                        <table className="table">
                          <tbody>
                            {/* <React.Fragment> */}
                            <tr>
                              <td
                                className="text-left"
                                style={{ fontWeight: "bold" }}
                              >
                                Source
                              </td>
                              <td
                                className="text-left"
                                style={{ fontWeight: "bold" }}
                              >
                                Orders
                              </td>
                              <td
                                className="text-left"
                                style={{ fontWeight: "bold" }}
                              >
                                Revenue
                              </td>
                            </tr>
                            <tr>
                              <td className="text-left">Flash</td>
                              <td className="text-left">10</td>
                              <td className="text-left">20000</td>
                            </tr>
                            <tr>
                              <td className="text-left">Opharma</td>
                              <td className="text-left">10</td>
                              <td className="text-left">40000</td>
                            </tr>
                            <tr>
                              <td className="text-left">MyPharma</td>
                              <td className="text-left">10</td>
                              <td className="text-left">20000</td>
                            </tr>
                            <tr>
                              <td className="text-left">&nbsp;&nbsp;</td>
                              <td className="text-left">30</td>
                              <td className="text-left">80000</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Col>
                    <Col md={6} mdOffset={1}>
                      <VectorMap
                        map={"world_mill"}
                        backgroundColor="transparent"
                        zoomOnScroll={false}
                        containerStyle={{
                          width: "100%",
                          height: "280px",
                        }}
                        containerClassName="map"
                        regionStyle={{
                          initial: {
                            fill: "#e4e4e4",
                            "fill-opacity": 0.9,
                            stroke: "none",
                            "stroke-width": 0,
                            "stroke-opacity": 0,
                          },
                        }}
                        series={{
                          regions: [
                            {
                              values: mapData,
                              scale: ["#AAAAAA", "#444444"],
                              normalizeFunction: "polynomial",
                            },
                          ],
                        }}
                      />
                    </Col>
                  </Row>
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={8}></Col>
          </Row>
          <Row>
            <Col md={6}>
              <Card
                title="2014 Sales"
                category="All products including Taxes"
                content={
                  <ChartistGraph
                    data={dataBar}
                    type="Bar"
                    options={optionsBar}
                    responsiveOptions={responsiveBar}
                  />
                }
                legend={
                  <div>
                    <i className="fa fa-circle text-info" /> Tesla Model S
                    <i className="fa fa-circle text-danger" /> BMW 5 Series
                  </div>
                }
                stats={
                  <div>
                    <i className="fa fa-check" /> Data information certified
                  </div>
                }
              />
            </Col>
            <Col md={6}>
              <Card
                title="Tasks"
                category="Backend development"
                content={
                  <table className="table">
                    <Tasks />
                  </table>
                }
                stats={
                  <div>
                    <i className="fa fa-history" /> Updated 3 minutes ago
                  </div>
                }
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

const Dashboard = () => {
  const user = getStoredUser();

  if (canViewOperationalDashboard(user)) {
    return <OperationalDashboard />;
  }

  return <LegacyDashboard />;
};

export default Dashboard;
